import "../styling/PaymentMethodsStyles.css";
import { devLog } from "../../../utils/devLog";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { flushSync } from "react-dom";
import api from "../../../api/api";
import { useCheckout } from "../../../context/CheckoutContext";
import { toast } from "react-toastify";
import { fetchCheckoutDetails } from "../../../api/api";
import { useTranslation } from "../../../hooks/useTranslation";
import { useStyles } from "../../../context/StyleContext";
import { useAdyenCard } from "../../../hooks/useAdyenCard";
import {
  mapStylesToAdyen,
  getAdyenEnvironment,
  getAdyenClientKey,
  validateAdyenClientKey,
  getAdyenClientKeySetupError,
  formatAdyenCardError,
  isIgnorableAdyenError,
  normalizeAdyenPaymentMethodsResponse,
  isAdyenPaymentMethodsReady,
  extractAdyenEncryptedCardData,
  isAdyenCardDataComplete,
  getLocaleFromCurrency,
  getCountryFromCurrency,
} from "../../../utils/adyenStyleMapper";
import { CALLBACKS, triggerCallback } from "../../../utils/commonUtils";
import { fetchSession, updateSession } from "../../../utils/paymentSession";
import {
  fetchGatewayConnection,
  processPayment,
  submitAdyenPaymentDetails,
} from "../../../api/api";
import { safeRedirect } from "../../../utils/redirectUrlBuilder";
import AdyenCardFormView from "./AdyenCardFormView";
import useWebSocket from "../../../hooks/useWebSocket";
import { formatPaymentError } from "../../../utils/formatPaymentError";
import {
  handleCitPaymentApiResponse,
  applyCitDeclineOrErrorUi,
  CIT_OUTCOME,
  parseCitPaymentResponse,
} from "../../../utils/paymentOutcome";

const extractAdyenAction = (data) => {
  if (!data) return null;
  const raw = data.action ?? data.action_object;
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
};

const AdyenCardForm = ({
  formData,
  itemDetails,
  billingPlatformId,
  routingEntries,
  planId,
  sessionId,
  prefetchedGatewayConnection,
  checkoutProcessDto,
  checkoutPageConfDto,

  // Hooks for Altruon variant
  onCardDataChange,
  isEmbeddedCheckout = false,
  onParentCallback
}) => {
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [gatewayConnection, setGatewayConnection] = useState(null);
  const [paymentMethodsResponse, setPaymentMethodsResponse] = useState(null);
  const [encryptedCardData, setEncryptedCardData] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState("");
  const [checkoutConfDto, setCheckoūtConfDto] = useState(
    checkoutPageConfDto || null
  );
  const [showProcessingModal, setProcessingModal] = useState(false);
  const [initializationComplete, setInitializationComplete] = useState(false);
  const [transactionId, setTransactionId] = useState(null);
  const [hostedPageRedirect, setHostedPageRedirect] = useState(null);
  const [awaiting3DS, setAwaiting3DS] = useState(false);
  const [threeDsMode, setThreeDsMode] = useState(null);

  // Refs
  const cardElementRef = useRef(null);
  const threeDsElementRef = useRef(null);
  const encryptedCardDataRef = useRef(null);
  const isProcessingRef = useRef(false);
  const mountTimeoutRef = useRef(null);
  const awaiting3DSRef = useRef(false);
  const pendingTransactionIdRef = useRef(null);
  const completeThreeDsDetailsRef = useRef(null);
  const detailsInFlightRef = useRef(false);
  const lastDetailsKeyRef = useRef(null);
  const lastResolvePayloadRef = useRef(null);
  const threeDsCompletedRef = useRef(false);
  const adyenPaymentDataRef = useRef(null);
  const threeDsMountedRef = useRef(false);

  // Hooks
  const { t } = useTranslation();
  const { styles } = useStyles();
  const {
    triggerPaymentMethodEvent,
    resetTrigger,
    clientIp,
    clientCountry,
    paymentMethodName,
    platformName,
    isHostedPageMode,
  } = useCheckout();

  const adyenRouting = useMemo(() => {
    return routingEntries?.find(
      (entry) => entry.paymentMethod === "card" && entry.platform === "adyen"
    );
  }, [routingEntries]);

  // Environment configuration
  const environment = gatewayConnection
    ? getAdyenEnvironment(gatewayConnection)
    : "test";
  const locale = itemDetails?.currencyCode
    ? getLocaleFromCurrency(itemDetails.currencyCode)
    : "en-US";
  const countryCode = itemDetails?.currencyCode
    ? getCountryFromCurrency(itemDetails.currencyCode)
    : "US";

  // Helpers
  const filterRoutingEntries = (entries, paymentMethod, platform) =>
    (entries || []).filter(
      (entry) =>
        entry.paymentMethod === paymentMethod && entry.platform === platform
    );

  const fetchPaymentMethods = useCallback(async () => {
    try {
      const amountMajor =
        itemDetails?.price != null ? Number(itemDetails.price) : undefined;
      const response = await api.post(
        `/api/checkout/v1/${sessionId}/adyen/payment-methods`,
        {
          currency: itemDetails?.currencyCode || "USD",
          ...(amountMajor != null && !Number.isNaN(amountMajor) && amountMajor > 0
            ? { amount: amountMajor }
            : {}),
          billing: {
            address: {
              country: formData?.billingAddress?.country || clientCountry || "US",
            },
          },
        }
      );
      if (response.status === 200 && response.data) {
        const normalized = normalizeAdyenPaymentMethodsResponse(response.data);
        if (!normalized) {
          throw new Error(
            "Adyen card payment method is not available. Check gateway configuration."
          );
        }
        setPaymentMethodsResponse(normalized);
        return normalized;
      } else {
        throw new Error(t("messages.error.paymentMethodsFailed"));
      }
    } catch (err) {
      console.error("Error fetching payment methods:", err);
      toast.error(t("messages.error.paymentMethodsFailed"));
      throw err;
    }
  }, [formData, itemDetails, sessionId, t]);

  useEffect(() => {
    if (checkoutPageConfDto) {
      setCheckoūtConfDto(checkoutPageConfDto);
      return;
    }
    if (billingPlatformId && planId) {
      loadCheckoutDetails();
    }
  }, [billingPlatformId, planId, checkoutPageConfDto]);

  const loadCheckoutDetails = async () => {
    try {
      const data = await fetchCheckoutDetails(billingPlatformId, planId);

      if (data?.checkoutPageConfDto) {
        setCheckoūtConfDto(data.checkoutPageConfDto);
      }
    } catch (error) {
      toast.error(t("messages.error.somethingWrong") || "Something went wrong.");
    }
  };

  const getMissingAdyenCardFields = (encryptedData) => {
    const missing = [];
    if (!encryptedData?.encryptedCardNumber) missing.push("cardNumber");
    if (!encryptedData?.encryptedExpiryMonth) missing.push("expiryMonth");
    if (!encryptedData?.encryptedExpiryYear) missing.push("expiryYear");
    if (!encryptedData?.encryptedSecurityCode) missing.push("securityCode");
    if (!encryptedData?.holderName) missing.push("holderName");
    return missing;
  };

  // Payload builders
  const buildPayload = (cardData, gateway_connection, latestSession) => {
    const holderName =
      cardData.holderName?.trim() ||
      [formData?.firstName, formData?.lastName].filter(Boolean).join(" ").trim();

    return {
    paymentData: {
      gatewayConnectionId: gateway_connection?.id,
      gatewayName: gateway_connection?.platform,
      paymentMethod: "card",
      // amount: itemDetails?.price || latestSession?.paymentData?.amount,
      // currency: itemDetails?.currencyCode || latestSession?.paymentData?.currency || "USD",
      additionalData: {
        "adyen.encryptedCardNumber": cardData.encryptedCardNumber,
        "adyen.encryptedExpiryMonth": cardData.encryptedExpiryMonth,
        "adyen.encryptedExpiryYear": cardData.encryptedExpiryYear,
        "adyen.encryptedSecurityCode": cardData.encryptedSecurityCode,
        "adyen.holderName": holderName,
        origin: typeof window !== "undefined" ? window.location.origin : undefined,
        ...(cardData.browserInfo ? { browserInfo: cardData.browserInfo } : {}),
      },
    },
    customerData: {
      firstName: formData?.firstName || latestSession?.customerData?.firstName,
      lastName: formData?.lastName || latestSession?.customerData?.lastname,
      email: formData?.email || latestSession?.customerData?.email,
      phone: formData?.phone || latestSession?.customerData?.phone || "9908734801",
      ip: clientIp,
      billingAddress: {
        country: formData?.billingAddress?.country || latestSession?.customerData?.billingAddress?.country || clientCountry || "US",
        zipCode: formData?.billingAddress?.zipCode || latestSession?.customerData?.billingAddress?.zipCode || "90210",
        street: formData?.billingAddress?.street || latestSession?.customerData?.billingAddress?.street || "",
        city: formData?.billingAddress?.city || latestSession?.customerData?.billingAddress?.city || "",
        state: formData?.billingAddress?.state || latestSession?.customerData?.billingAddress?.state || "",
        number: "1234",
      },
    },
    billingData: {
      planId,
      billingPlatformId: latestSession?.billingData?.billingPlatformId || "",
      billingPlatformName: itemDetails?.billingPlatformName || latestSession?.billingData?.billingPlatformName || "",
    },
  };
  };


  const handleProcessPayment = async (payload, cardData) => {
    if (isEmbeddedCheckout) {
      onParentCallback?.({
        type: "finalizeFormData",
        data: {
          paymentMethod: "card",
          gatewayName: "adyen",
          encryptedCardNumber: cardData.encryptedCardNumber,
          encryptedExpiryMonth: cardData.encryptedExpiryMonth,
          encryptedExpiryYear: cardData.encryptedExpiryYear,
          encryptedSecurityCode: cardData.encryptedSecurityCode,
          holderName: cardData.holderName,
        },
      });
      await new Promise((r) => setTimeout(r, 800));
    }

    if (!isEmbeddedCheckout) {
      await updateSession(sessionId, payload);
    }

    return processPayment(billingPlatformId, sessionId, payload);
  };

  // Initialize gateway connection
  useEffect(() => {
    if (prefetchedGatewayConnection && !gatewayConnection) {
      setGatewayConnection(prefetchedGatewayConnection);
      return;
    }

    const initializeGateway = async () => {
      try {
        const routingEntry = filterRoutingEntries(
          routingEntries,
          "card",
          "adyen"
        );
        if (routingEntry.length > 0) {
          const connection = await fetchGatewayConnection(routingEntry[0], t);
          setGatewayConnection(connection);
        }
      } catch (err) {
        console.error("Failed to initialize gateway:", err);
      }
    };

    if (routingEntries && routingEntries.length > 0 && !gatewayConnection) {
      initializeGateway();
    }
  }, [
    routingEntries,
    prefetchedGatewayConnection,
    gatewayConnection,
    fetchGatewayConnection,
  ]);

  // Initialize payment methods
  useEffect(() => {
    const initialize = async () => {
      try {
        if (!gatewayConnection) return;

        if (!paymentMethodsResponse) {
          await fetchPaymentMethods();
        }
        setInitializationComplete(true);
      } catch (err) {
        console.error("Initialization error:", err);
        setError(err.message || "Failed to initialize payment form");
      }
    };

    if (gatewayConnection && !initializationComplete) {
      initialize();
    }
  }, [
    gatewayConnection,
    paymentMethodsResponse,
    initializationComplete,
    fetchPaymentMethods,
  ]);

  const adyenClientKey = getAdyenClientKey(gatewayConnection);

  const clientKeyValidation = useMemo(() => {
    if (!adyenClientKey) {
      return { valid: false, reason: "missing" };
    }
    return validateAdyenClientKey(adyenClientKey, environment);
  }, [adyenClientKey, environment]);

  const canInitializeAdyen = Boolean(
    initializationComplete &&
      clientKeyValidation.valid &&
      isAdyenPaymentMethodsReady(paymentMethodsResponse)
  );

  const adyenCardConfiguration = useMemo(
    () => ({
      enableStoreDetails: false,
      // Avoid stale bin-lookup responses after decline + card change (unknownError).
      doBinLookup: false,
    }),
    []
  );

  // Adyen card hook — only init when clientKey + /paymentMethods are ready (Adyen docs)
  const {
    cardComponent,
    ready: cardReady,
    configReady,
    error: cardError,
    loading: sdkLoading,
    mount,
    submit,
    handleAction,
    createFromAction,
    clearError: clearCardError,
  } = useAdyenCard({
    enabled: canInitializeAdyen,
    clientKey: adyenClientKey,
    environment,
    paymentMethodsResponse: canInitializeAdyen ? paymentMethodsResponse : null,
    locale,
    countryCode,
    styles: mapStylesToAdyen(styles),
    cardConfiguration: adyenCardConfiguration,
    onReady: () => {
      devLog("Adyen Card Component is ready");
    },
    onError: (err) => {
      if (isIgnorableAdyenError(err)) {
        clearCardError();
        return;
      }
      console.error("Adyen Card error:", err);
      const msg =
        formatAdyenCardError(err, t) ||
        (typeof err === "string" ? err : err?.message) ||
        "Card validation error";
      setError(msg);
      triggerCallback(CALLBACKS.DATA_REQUIRED, {
        message: "Missing or invalid card details.",
        details: msg,
        requiredFields: [
          "cardNumber",
          "expiryDate",
          "securityCode",
          "holderName",
        ],
      });
    },
    onBrand: (brandObject) => {
      // Card brand updated
    },
    onChange: (state) => {
      clearCardError();
      try {
        cardComponent?.setStatus?.("ready");
      } catch {
        // ignore
      }
      setError("");
      const cardData = extractAdyenEncryptedCardData(state);
      if (cardData) {
        encryptedCardDataRef.current = cardData;
        setEncryptedCardData(cardData);
        onCardDataChange?.(cardData);
      } else if (!state?.isValid) {
        encryptedCardDataRef.current = null;
        setEncryptedCardData(null);
        onCardDataChange?.(null);
      }
    },
    onAdditionalDetails: async (additionalDetailsData, _component, actions) => {
      try {
        await completeThreeDsDetailsRef.current?.(additionalDetailsData, actions);
      } catch (err) {
        console.error("Error processing 3DS result:", err);
        setError(
          formatPaymentError(
            err?.response?.data?.error ||
              err?.response?.data?.message ||
              t("messages.error.paymentFailed"),
            t
          )
        );
        isProcessingRef.current = false;
        awaiting3DSRef.current = false;
        setAwaiting3DS(false);
        setThreeDsMode(null);
        if (!isEmbeddedCheckout) {
          setProcessingModal(false);
        }
      }
    },
    onPaymentCompleted: (result) => {
      if (threeDsCompletedRef.current) {
        return;
      }
      const resultCode = result?.resultCode;
      if (resultCode && resultCode !== "Authorised") {
        return;
      }
      const txnId = pendingTransactionIdRef.current;
      if (!txnId) {
        return;
      }
      threeDsCompletedRef.current = true;
      awaiting3DSRef.current = false;
      setAwaiting3DS(false);
      setThreeDsMode(null);
      setTransactionId(txnId);
      if (isEmbeddedCheckout) {
        onParentCallback?.({ type: "success", transactionId: txnId });
      } else {
        setTransactionStatus("success");
        setShowStatusModal(true);
      }
    },
  });

  const closeProcessingUi = useCallback(() => {
    if (!isEmbeddedCheckout) {
      setProcessingModal(false);
    }
  }, [isEmbeddedCheckout]);

  const applyPaymentSuccessUi = useCallback(
    (response, parsed) => {
      if (response?.data?.hosted_redirect_page) {
        setHostedPageRedirect(response.data.hosted_redirect_page);
      }

      const txnId =
        parsed?.transactionId ||
        response?.data?.newTransactionId ||
        response?.data?.transactionId;
      if (txnId) {
        pendingTransactionIdRef.current = txnId;
        setTransactionId(txnId);
      }

      const redirectUrl =
        parsed?.redirectUrl ||
        (response?.data?.redirect_url
          ? `${response.data.redirect_url}${
              response.data.redirect_url.includes("?") ? "&" : "?"
            }trxId=${txnId || response.data.transactionId}`
          : null);

      if (isEmbeddedCheckout) {
        if (redirectUrl) {
          onParentCallback?.({
            type: "actionRequired",
            action: "REDIRECT",
            url: redirectUrl,
          });
          safeRedirect(redirectUrl, { target: "blank" });
        } else {
          onParentCallback?.({
            type: "success",
            transactionId: txnId || response?.data?.transactionId,
          });
        }
        return;
      }

      if (redirectUrl) {
        safeRedirect(redirectUrl, { target: "self" });
        return;
      }

      setTransactionStatus("success");
      setShowStatusModal(true);
    },
    [isEmbeddedCheckout, onParentCallback]
  );

  const presentAdyenAction = useCallback(
    async (action) => {
      if (!action) {
        throw new Error("Missing Adyen 3DS action");
      }
      if (action.paymentData) {
        adyenPaymentDataRef.current = action.paymentData;
      }
      const mode =
        action.type === "redirect" || action.subtype === "fingerprint"
          ? "fingerprint"
          : "challenge";
      awaiting3DSRef.current = true;
      flushSync(() => {
        setAwaiting3DS(true);
        setThreeDsMode(mode);
      });
      isProcessingRef.current = false;
      setLoading(false);
      closeProcessingUi();

      if (action.type === "redirect") {
        await handleAction(action);
        return;
      }

      if (threeDsMountedRef.current && action.type !== "redirect") {
        return;
      }

      if (!threeDsElementRef.current) {
        throw new Error("3DS container not ready");
      }
      createFromAction(action, threeDsElementRef.current);
      threeDsMountedRef.current = true;
    },
    [closeProcessingUi, createFromAction, handleAction]
  );

  const completeThreeDsDetails = useCallback(
    async (additionalDetails, actions) => {
      const detailsKey = JSON.stringify(additionalDetails?.details ?? {});
      if (threeDsCompletedRef.current) {
        return;
      }
      if (detailsInFlightRef.current) {
        return;
      }
      if (lastDetailsKeyRef.current === detailsKey && lastResolvePayloadRef.current) {
        actions?.resolve?.(lastResolvePayloadRef.current);
        return;
      }

      detailsInFlightRef.current = true;
      lastDetailsKeyRef.current = detailsKey;

      const citUi = {
        isHostedPageMode,
        isEmbeddedCheckout,
        t,
        setError,
        setTransactionStatus,
        setShowStatusModal,
        onParentCallback,
        toast,
      };

      try {
        const txnId = pendingTransactionIdRef.current || transactionId;
        if (!txnId) {
          throw new Error("Missing transaction id for 3DS completion");
        }

        if (additionalDetails?.paymentData) {
          adyenPaymentDataRef.current = additionalDetails.paymentData;
        }

        const detailsResponse = await submitAdyenPaymentDetails({
          details: additionalDetails.details,
          paymentData:
            additionalDetails.paymentData || adyenPaymentDataRef.current,
          transactionId: txnId,
        });

        const resultCode = detailsResponse.data?.resultCode;
        const action = extractAdyenAction(detailsResponse.data);
        const resolvePayload = action ? { resultCode, action } : { resultCode };
        lastResolvePayloadRef.current = resolvePayload;

        if (!action) {
          threeDsCompletedRef.current = true;
          if (typeof actions?.resolve === "function") {
            actions.resolve({ resultCode });
          }
        } else if (action.subtype === "fingerprint") {
          if (typeof actions?.resolve === "function") {
            actions.resolve(resolvePayload);
          } else {
            await presentAdyenAction(action);
          }
          return;
        } else {
          flushSync(() => {
            setAwaiting3DS(true);
            setThreeDsMode("challenge");
          });
          if (typeof actions?.resolve === "function") {
            actions.resolve(resolvePayload);
            return;
          }
          await presentAdyenAction(action);
          return;
        }
        awaiting3DSRef.current = false;
        setAwaiting3DS(false);
        setThreeDsMode(null);

        if (detailsResponse.status === 200 && detailsResponse.data?.success) {
          setTransactionId(txnId);
          if (isEmbeddedCheckout) {
            onParentCallback?.({ type: "success", transactionId: txnId });
          } else {
            setTransactionStatus("success");
            setShowStatusModal(true);
          }
        } else {
          const msg =
            detailsResponse.data?.error ||
            resultCode ||
            t("messages.error.paymentFailed");
          applyCitDeclineOrErrorUi(
            { outcome: CIT_OUTCOME.ERROR, message: formatPaymentError(msg, t) },
            citUi
          );
        }
      } catch (err) {
        lastDetailsKeyRef.current = null;
        awaiting3DSRef.current = false;
        setAwaiting3DS(false);
        setThreeDsMode(null);
        throw err;
      } finally {
        detailsInFlightRef.current = false;
      }
    },
    [
      isEmbeddedCheckout,
      isHostedPageMode,
      onParentCallback,
      presentAdyenAction,
      t,
      transactionId,
    ]
  );

  useEffect(() => {
    completeThreeDsDetailsRef.current = completeThreeDsDetails;
  }, [completeThreeDsDetails]);

  const handlePayNow = useCallback(
    async () => {
      if (isProcessingRef.current || awaiting3DSRef.current) {
        console.warn("Adyen payment already in progress");
        return;
      }

      threeDsCompletedRef.current = false;
      lastDetailsKeyRef.current = null;
      lastResolvePayloadRef.current = null;
      adyenPaymentDataRef.current = null;
      threeDsMountedRef.current = false;

      if (!isEmbeddedCheckout) {
        setProcessingModal(true);
      }

      const citUi = {
        isHostedPageMode,
        isEmbeddedCheckout,
        t,
        setError,
        setTransactionStatus,
        setShowStatusModal,
        onParentCallback,
        toast,
      };

      if (!configReady) {
        setError(
          t("paymentForms.card.loadingSDK") ||
            "Card form is still loading. Please wait."
        );
        closeProcessingUi();
        return;
      }

      let cardData = encryptedCardDataRef.current;
      try {
        const submittedState = await submit();
        const fromSubmit = extractAdyenEncryptedCardData(submittedState);
        if (fromSubmit) {
          cardData = fromSubmit;
          encryptedCardDataRef.current = fromSubmit;
          setEncryptedCardData(fromSubmit);
        }
      } catch (submitErr) {
        setError(
          formatPaymentError(submitErr?.message, t) ||
            t("paymentForms.card.tokenError")
        );
        closeProcessingUi();
        return;
      }

      const holderName =
        cardData?.holderName?.trim() ||
        [formData?.firstName, formData?.lastName].filter(Boolean).join(" ").trim();
      if (!isAdyenCardDataComplete({ ...cardData, holderName })) {
        const missing = getMissingAdyenCardFields({
          ...cardData,
          holderName,
        });
        console.warn("[Adyen] incomplete card data at submit:", missing);
        setError(t("paymentForms.card.tokenError"));
        closeProcessingUi();
        return;
      }
      if (holderName && !cardData.holderName) {
        cardData = { ...cardData, holderName };
        encryptedCardDataRef.current = cardData;
      }

      isProcessingRef.current = true;
      setLoading(true);
      setError("");

      try {
        cardData = encryptedCardDataRef.current;
        if (!cardData) {
          throw new Error("No encrypted card data available");
        }

        const gateway_connection =
          gatewayConnection || (await fetchGatewayConnection(adyenRouting, t));
        const latestSession = await fetchSession(sessionId);
        const payload = buildPayload(cardData, gateway_connection, latestSession);
        if (!payload) {
          return;
        }

        const response = await handleProcessPayment(payload, cardData);
        const { handled, parsed } = handleCitPaymentApiResponse(response, citUi);
        if (handled) {
          return;
        }

        if (response.status === 200) {
          const txnId =
            response.data?.newTransactionId || response.data?.transactionId;
          if (txnId) {
            pendingTransactionIdRef.current = txnId;
          }

          const nextAction = extractAdyenAction(response.data);
          if (nextAction) {
            try {
              await presentAdyenAction(nextAction);
            } catch (actionErr) {
              awaiting3DSRef.current = false;
              setAwaiting3DS(false);
              setThreeDsMode(null);
              throw actionErr;
            }
            return;
          }

          const outcomeParsed =
            parsed || parseCitPaymentResponse(response, t);
          if (outcomeParsed.outcome === CIT_OUTCOME.REDIRECT && outcomeParsed.redirectUrl) {
            applyPaymentSuccessUi(response, outcomeParsed);
            return;
          }

          if (outcomeParsed.outcome === CIT_OUTCOME.PENDING) {
            setTransactionId(txnId);
            setError(
              formatPaymentError(
                outcomeParsed.message ||
                  t("messages.info.pendingStatus"),
                t
              )
            );
            return;
          }

          applyPaymentSuccessUi(response, outcomeParsed);
        } else {
          applyCitDeclineOrErrorUi(
            {
              outcome: CIT_OUTCOME.ERROR,
              message: t("messages.error.somethingWrong"),
            },
            citUi
          );
          throw new Error(t("messages.error.somethingWrong"));
        }
      } catch (e) {
        if (e?.response && handleCitPaymentApiResponse(e.response, citUi).handled) {
          return;
        }
        applyCitDeclineOrErrorUi(
          {
            outcome: CIT_OUTCOME.ERROR,
            message: formatPaymentError(
              e?.response?.data?.message || e?.message,
              t
            ),
          },
          citUi
        );
      } finally {
        setLoading(false);
        isProcessingRef.current = false;
        clearCardError();
        if (!awaiting3DSRef.current) {
          closeProcessingUi();
        }
      }
    },
    [
      adyenRouting,
      applyPaymentSuccessUi,
      billingPlatformId,
      closeProcessingUi,
      configReady,
      formData,
      gatewayConnection,
      handleProcessPayment,
      presentAdyenAction,
      submit,
      clearCardError,
      isEmbeddedCheckout,
      isHostedPageMode,
      onParentCallback,
      sessionId,
      t,
    ]
  );

  const isAdyenCardMounted = useCallback((element) => {
    if (!element) return false;
    if (awaiting3DSRef.current) return true;
    return Boolean(
      element.querySelector(
        [
          ".adyen-checkout__card__form",
          ".adyen-checkout__loading-input__spinner",
          ".adyen-checkout__threeds2",
          "iframe[name*='threeDS']",
        ].join(", ")
      )
    );
  }, []);

  // Mount Adyen card component (mount target must stay empty — no loading placeholders inside)
  useEffect(() => {
    if (mountTimeoutRef.current) {
      clearTimeout(mountTimeoutRef.current);
      mountTimeoutRef.current = null;
    }

    // Never remount while a native 3DS challenge is on screen — the card form
    // is hidden, so the usual "is the form in the DOM?" check would remount.
    if (awaiting3DSRef.current) {
      return;
    }

    // Mount as soon as the Card instance exists — onConfigSuccess runs after mount.
    if (!canInitializeAdyen || !cardComponent || !cardElementRef.current) {
      return;
    }

    if (isAdyenCardMounted(cardElementRef.current)) {
      return;
    }

    mountTimeoutRef.current = setTimeout(async () => {
      try {
        if (
          awaiting3DSRef.current ||
          !cardElementRef.current ||
          isAdyenCardMounted(cardElementRef.current)
        ) {
          return;
        }
        await mount(cardElementRef.current);
      } catch (err) {
        console.error("Failed to mount card component:", err);
        setError(err?.message || "Failed to load payment form");
      } finally {
        if (mountTimeoutRef.current) {
          clearTimeout(mountTimeoutRef.current);
          mountTimeoutRef.current = null;
        }
      }
    }, 300);

    return () => {
      if (mountTimeoutRef.current) {
        clearTimeout(mountTimeoutRef.current);
        mountTimeoutRef.current = null;
      }
    };
  }, [canInitializeAdyen, cardComponent, mount, isAdyenCardMounted]);

  // Handle payment trigger from context
  useEffect(() => {
    if (triggerPaymentMethodEvent &&
      (isEmbeddedCheckout || (platformName === "adyen" && paymentMethodName === "card"))) {
      handlePayNow();
      resetTrigger();
    }
  }, [
    triggerPaymentMethodEvent,
    platformName,
    paymentMethodName,
    handlePayNow,
    resetTrigger,
  ]);


  // Listen websocket only for SDK version
  useWebSocket(isEmbeddedCheckout && transactionId, (event) => {
        const data = JSON.parse(event.data);

        if (!data?.status) return;

        if (data.status === "success") {
            onParentCallback?.({
                type: "success",
                transactionId,
            });
        }

        if (data.status === "failed") {
            onParentCallback?.({
                type: "failure",
            });
        }
    },
      undefined,
      undefined,
      (err) => {
          console.error("CARD WS error", err);
      }
  );

  return (
    <AdyenCardFormView
      t={t}
      loading={loading}
      error={error}
      cardError={cardError}
      sdkLoading={sdkLoading}
      cardReady={cardReady}
      initializationComplete={initializationComplete}
      configReady={configReady}
      adyenSetupBlocked={
        initializationComplete && !canInitializeAdyen && !sdkLoading
      }
      adyenSetupError={
        initializationComplete && !clientKeyValidation.valid
          ? getAdyenClientKeySetupError(clientKeyValidation.reason, t)
          : null
      }
      cardElementRef={cardElementRef}
      threeDsElementRef={threeDsElementRef}
      awaiting3DS={awaiting3DS}
      threeDsMode={threeDsMode}
      showProcessingModal={showProcessingModal}
      setProcessingModal={setProcessingModal}
      showStatusModal={showStatusModal}
      setShowStatusModal={setShowStatusModal}
      transactionStatus={transactionStatus}
      checkoutConfDto={checkoutConfDto}
      transactionId={transactionId}
      hostedPageRedirect={hostedPageRedirect}
    />
  );
};

export default AdyenCardForm;
